import React from 'react';
import { CalendarDays, Clock, Car, CreditCard, Tag } from 'lucide-react';
import type { BookingState } from './BookingWizard';

interface Props {
  booking: BookingState;
  currentStep: number;
}

export default function BookingSummaryPanel({ booking, currentStep }: Props) {
  const { service, dateTime, clientDetails, paymentType } = booking;

  const depositAmount = service ? Math.round(service.price * 0.3) : 0;
  const addonsTotal = service?.addons.reduce((sum, a) => sum + a.price, 0) ?? 0;
  const subtotal = (service?.price ?? 0) + addonsTotal;
  const dueNow = paymentType === 'deposit' ? depositAmount : subtotal;

  return (
    <div className="sticky top-24 bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border">
        <p className="text-xs font-semibold tracking-widest text-primary uppercase">Booking Summary</p>
      </div>

      <div className="p-5 space-y-4">
        {/* Service */}
        {service ? (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-foreground">{service.name}</p>
                {service.vehicleSize && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Car size={11} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{service.vehicleSize}</span>
                  </div>
                )}
              </div>
              <p className="text-sm font-bold text-foreground tabular-nums whitespace-nowrap">
                {service.priceLabel || (service.price > 0 ? `$${service.price}` : '—')}
              </p>
            </div>

            {/* Add-ons */}
            {service.addons.length > 0 && (
              <div className="pl-3 border-l-2 border-primary/30 space-y-1">
                {service.addons.map((addon) => (
                  <div key={`summary-addon-${addon.id}`} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Tag size={11} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{addon.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      +{addon.priceLabel || `$${addon.price}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="py-3 text-center">
            <p className="text-xs text-muted-foreground">No service selected yet</p>
          </div>
        )}

        {/* Date & Time */}
        {currentStep >= 2 && dateTime && (
          <div className="pt-3 border-t border-border space-y-2">
            <div className="flex items-center gap-2">
              <CalendarDays size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm text-foreground">{dateTime.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-primary flex-shrink-0" />
              <span className="text-sm text-foreground">{dateTime.time}</span>
            </div>
          </div>
        )}

        {/* Vehicle */}
        {currentStep >= 3 && clientDetails && (
          <div className="pt-3 border-t border-border">
            <div className="flex items-start gap-2">
              <Car size={14} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground">
                  {clientDetails.vehicleYear} {clientDetails.vehicleMake} {clientDetails.vehicleModel}
                </p>
                <p className="text-xs text-muted-foreground">{clientDetails.vehicleColor}</p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing breakdown */}
        {service && (
          <div className="pt-3 border-t border-border space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Service</span>
              <span className="text-foreground tabular-nums">
                {service.priceLabel || `$${service.price}`}
              </span>
            </div>
            {service.addons.map((addon) => (
              <div key={`breakdown-addon-${addon.id}`} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{addon.name}</span>
                <span className="text-foreground tabular-nums">+{addon.priceLabel || `$${addon.price}`}</span>
              </div>
            ))}
            {addonsTotal > 0 && (
              <div className="flex items-center justify-between text-xs pt-1 border-t border-border/50">
                <span className="text-muted-foreground">Subtotal (est.)</span>
                <span className="text-foreground tabular-nums">${subtotal.toLocaleString()}</span>
              </div>
            )}

            {currentStep >= 3 && (
              <div className="pt-2 border-t border-border">
                <div className="flex items-center gap-1.5 mb-2">
                  <CreditCard size={12} className="text-primary" />
                  <span className="text-xs font-medium text-primary">
                    {paymentType === 'deposit' ? '30% Deposit' : 'Full Payment'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Due Now</span>
                  <span className="text-xl font-extrabold text-primary tabular-nums">${dueNow.toLocaleString()}</span>
                </div>
                {paymentType === 'deposit' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Remaining ${(subtotal - depositAmount).toLocaleString()} due on appointment day
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}